import { Package, Plus, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useBillingCycles, useDeleteSubscriptionPlan, useSubscriptionPlans } from '@/hooks/billing';
import { SubscriptionPlan, TranslationParams } from '@/types/billing';
import { SubscriptionPricingCard } from './SubscriptionPricingCard';
import { SubscriptionPlanVariantDialog } from './SubscriptionPlanVariantDialog';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface SubscriptionPlansListProps {
  productId: string;
  onEditPlan: (_plan: SubscriptionPlan) => void;
  defaultCurrency?: string;
  planType?: 'individual' | 'teams';
  currentLanguage?: string;
  showTranslations?: boolean;
  activeCurrencies?: string[];
}

export function SubscriptionPlansList({ 
  productId, 
  onEditPlan,
  defaultCurrency = 'USD',
  planType,
  currentLanguage = 'en',
  showTranslations = false,
  activeCurrencies
}: SubscriptionPlansListProps) {

  const { t } = useTranslation();
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'visible' | 'hidden'>('all');
  const [variantBasePlan, setVariantBasePlan] = useState<SubscriptionPlan | null>(null);
  const [showVariantDialog, setShowVariantDialog] = useState(false);
  const [expandedVariants, setExpandedVariants] = useState<Record<string, boolean>>({});
  const [planToDelete, setPlanToDelete] = useState<SubscriptionPlan | null>(null);
  const deletePlan = useDeleteSubscriptionPlan();
  const { data: billingCycles = [] } = useBillingCycles(productId);
  const billingCycleNameById = new Map(
    billingCycles.map((cycle) => [
      cycle.id,
      cycle.displayName || cycle.display_name || cycle.name || cycle.id,
    ])
  );

  console.log('SubscriptionPlansList - planType:', planType);

  // Build translation parameters
  const translationParams: TranslationParams | undefined = showTranslations
    ? {
        lang: currentLanguage,
        includeTranslations: true,
      }
    : undefined;

  // Fetch subscription plans with translation support
  const basePlanFilters = {
    ...(planType ? { type: planType } : {}),
    isVariant: false,
  };

  const { 
    data: basePlans = [], 
    isLoading: basePlansLoading, 
    error: basePlansError 
  } = useSubscriptionPlans(
    productId,
    basePlanFilters,
    { field: 'display-order' },
    ['pricing', 'prices', 'features', 'credit-settings'],
    translationParams,
    { perPage: 100 }
  );

  const { 
    data: variants = [], 
    isLoading: variantsLoading, 
    error: variantsError 
  } = useSubscriptionPlans(
    productId,
    { ...(planType ? { type: planType } : {}), isVariant: true },
    { field: '-created-at' },
    ['pricing', 'prices', 'features', 'credit-settings'],
    translationParams,
    { perPage: 100 },
    { enabled: true }
  );

  // Archive and delete mutations (keeping for future use)
  // const archiveMutation = useArchiveSubscriptionPlan();
  // const deleteMutation = useDeleteSubscriptionPlan();

  type PricingByCycle = Record<string, { prices: Record<string, number>; perSeatPrices: Record<string, number> }>;

  const normalizeCurrencyKey = (currency: string) => currency.trim().toUpperCase();

  const resolveCycleId = (months: number) => {
    const cycleMatch = billingCycles.find((cycle) => cycle.months === months);
    if (cycleMatch?.id) {
      return cycleMatch.id;
    }
    if (months === 1) return 'monthly';
    if (months === 12) return 'annual';
    return undefined;
  };

  const buildPricingByCycle = (plan: SubscriptionPlan): PricingByCycle => {
    const pricingByCycle: PricingByCycle = {};
    const pricesArray = Array.isArray(plan.pricesArray) ? plan.pricesArray : [];

    pricesArray.forEach((price) => {
      const cycleId = (price.billingCycleId as string) || (price.billing_cycle_id as string);
      if (!cycleId) return;

      const priceRecord = price as Record<string, unknown>;
      const currencyRaw = (price.currency as string) || defaultCurrency;
      const currency = normalizeCurrencyKey(currencyRaw);
      const amount = (price.basePrice as number) ?? (price.base_price as number);
      const perSeatPrice = (priceRecord.perSeatPrice as number | null) ?? (priceRecord.per_seat_price as number | null);

      if (!pricingByCycle[cycleId]) {
        pricingByCycle[cycleId] = { prices: {}, perSeatPrices: {} };
      }

      if (typeof amount === 'number') {
        pricingByCycle[cycleId].prices[currency] = amount;
      }

      if (typeof perSeatPrice === 'number') {
        pricingByCycle[cycleId].perSeatPrices[currency] = perSeatPrice;
      }
    });

    const pricing = Array.isArray(plan.pricing) ? plan.pricing : [];
    pricing.forEach((entry) => {
      const cycleId = (entry as { billingCycleId?: string; billing_cycle_id?: string }).billingCycleId ??
        (entry as { billing_cycle_id?: string }).billing_cycle_id;
      if (!cycleId) return;

      const prices = (entry as { prices?: Record<string, number> }).prices ?? {};
      const perSeatPrices = (entry as { pricesPerAdditionalSeat?: Record<string, number> }).pricesPerAdditionalSeat ?? {};

      if (!pricingByCycle[cycleId]) {
        pricingByCycle[cycleId] = { prices: {}, perSeatPrices: {} };
      }

      Object.entries(prices).forEach(([currencyKey, amount]) => {
        pricingByCycle[cycleId].prices[normalizeCurrencyKey(currencyKey)] = amount;
      });

      Object.entries(perSeatPrices).forEach(([currencyKey, amount]) => {
        pricingByCycle[cycleId].perSeatPrices[normalizeCurrencyKey(currencyKey)] = amount;
      });
    });

    return pricingByCycle;
  };

  const resolveCreditSettings = (plan: SubscriptionPlan) => {
    const planRecord = plan as Omit<SubscriptionPlan, 'creditSettings'> & {
      creditSettings?: unknown;
      credit_settings?: unknown;
      'credit-settings'?: unknown;
    };
    const rawSettings =
      planRecord.creditSettings ?? planRecord.credit_settings ?? planRecord['credit-settings'];
    if (!rawSettings || typeof rawSettings !== 'object') {
      return { includeCredits: false, creditsPerCycle: 0 };
    }

    const settings = rawSettings as Record<string, unknown>;
    const attributes =
      settings.attributes && typeof settings.attributes === 'object'
        ? (settings.attributes as Record<string, unknown>)
        : null;
    const source = attributes ?? settings;

    const includeCredits = Boolean(
      source.includeCredits ??
        source.includesCredits ??
        source.includes_credits ??
        source.include_credits ??
        source['include-credits'],
    );

    const creditsRaw =
      source.creditsPerCycle ??
      source.credits_per_cycle ??
      source['credits-per-cycle'];
    const creditsPerCycle =
      typeof creditsRaw === 'number'
        ? creditsRaw
        : typeof creditsRaw === 'string'
          ? Number(creditsRaw)
          : 0;

    return {
      includeCredits,
      creditsPerCycle: Number.isFinite(creditsPerCycle) ? creditsPerCycle : 0,
    };
  };

  const getPriceForCycle = (pricingByCycle: PricingByCycle, cycleId: string | undefined, currency: string) => {
    if (!cycleId) return undefined;
    const cyclePricing = pricingByCycle[cycleId];
    if (!cyclePricing) return undefined;
    const currencyKey = normalizeCurrencyKey(currency);
    if (cyclePricing.prices[currencyKey] !== undefined) {
      return cyclePricing.prices[currencyKey];
    }
    const fallbackAmount = Object.values(cyclePricing.prices)[0];
    return typeof fallbackAmount === 'number' ? fallbackAmount : undefined;
  };

  const getPerSeatPriceForCycle = (pricingByCycle: PricingByCycle, cycleId: string | undefined, currency: string) => {
    if (!cycleId) return undefined;
    const cyclePricing = pricingByCycle[cycleId];
    if (!cyclePricing) return undefined;
    const currencyKey = normalizeCurrencyKey(currency);
    if (cyclePricing.perSeatPrices[currencyKey] !== undefined) {
      return cyclePricing.perSeatPrices[currencyKey];
    }
    const fallbackAmount = Object.values(cyclePricing.perSeatPrices)[0];
    return typeof fallbackAmount === 'number' ? fallbackAmount : undefined;
  };

  const isLoading = basePlansLoading || variantsLoading;
  const error = basePlansError ?? variantsError;
  const resolveIsAvailableInCheckout = (plan: SubscriptionPlan): boolean => {
    const candidate = (plan as SubscriptionPlan & { isAvailableInCheckout?: boolean; is_available_in_checkout?: boolean })
      .isAvailableInCheckout;
    const legacy = (plan as SubscriptionPlan & { isAvailableInCheckout?: boolean; is_available_in_checkout?: boolean })
      .is_available_in_checkout;
    // Default to true when backend doesn't send the flag (older API / caches).
    return Boolean(candidate ?? legacy ?? true);
  };

  const variantsByParentIdAll = useMemo(() => {
    return variants.reduce<Record<string, SubscriptionPlan[]>>((acc, plan) => {
      const parentId = plan.parentPlanId ?? plan.parent_plan_id;
      if (!parentId) return acc;
      if (!acc[parentId]) acc[parentId] = [];
      acc[parentId].push(plan);
      return acc;
    }, {});
  }, [variants]);

  const filteredBasePlans = useMemo(() => {
    if (visibilityFilter === 'all') return basePlans;
    const wantVisible = visibilityFilter === 'visible';
    return basePlans.filter((plan) => resolveIsAvailableInCheckout(plan) === wantVisible);
  }, [basePlans, visibilityFilter]);

  const filteredVariants = useMemo(() => {
    if (visibilityFilter === 'all') return variants;
    const wantVisible = visibilityFilter === 'visible';
    return variants.filter((plan) => resolveIsAvailableInCheckout(plan) === wantVisible);
  }, [variants, visibilityFilter]);

  const variantsByParentId = filteredVariants.reduce<Record<string, SubscriptionPlan[]>>((acc, plan) => {
    const parentId = plan.parentPlanId ?? plan.parent_plan_id;
    if (!parentId) return acc;
    if (!acc[parentId]) acc[parentId] = [];
    acc[parentId].push(plan);
    return acc;
  }, {});

  const deleteDisabledByPlanId = useMemo(() => {
    const disabled: Record<string, string> = {};
    basePlans.forEach((plan) => {
      // Use all variants for delete protection, regardless of filter.
      const planVariants = variantsByParentIdAll[plan.id] ?? [];
      if (planVariants.length > 0) {
        disabled[plan.id] = t('products.subscriptionPlansList.deleteBlockedVariants');
      }
    });
    return disabled;
  }, [basePlans, t, variantsByParentIdAll]);

  const transformPlanData = (plan: SubscriptionPlan) => {
    // Log the raw plan data to debug
    console.log(`Transforming plan ${plan.name}:`, {
      includedSeats: plan.includedSeats,
      included_seats: (plan as SubscriptionPlan & { included_seats?: number }).included_seats,
      pricesArray: plan.pricesArray
    });
    
    // Find monthly and annual prices from the prices array
    const pricingByCycle = buildPricingByCycle(plan);
    const cycleMonthsById = new Map(billingCycles.map((cycle) => [cycle.id, cycle.months]));
    const findCycleIdByMonths = (months: number) => {
      const fromPricing = Object.keys(pricingByCycle).find((cycleId) => cycleMonthsById.get(cycleId) === months);
      return fromPricing ?? resolveCycleId(months);
    };
    const monthlyCycleId = findCycleIdByMonths(1);
    const annualCycleId = findCycleIdByMonths(12);
    const monthlyPrice = getPriceForCycle(pricingByCycle, monthlyCycleId, defaultCurrency) ?? 29;
    const annualPrice = getPriceForCycle(pricingByCycle, annualCycleId, defaultCurrency) ?? 290;
    const monthlyPerSeatPrice = getPerSeatPriceForCycle(pricingByCycle, monthlyCycleId, defaultCurrency);

    const annualMonthlyPrice = Math.round((annualPrice / 12) * 100) / 100;
    const annualDiscount =
      monthlyPrice > 0 ? Math.round((1 - (annualPrice / (monthlyPrice * 12))) * 100) : 0;
    
    // Features are now directly available as strings
    const features: string[] = Array.isArray(plan.features) ? plan.features : [];
    
    // Format credit info from credit settings (supports multiple backend shapes)
    const { includeCredits, creditsPerCycle } = resolveCreditSettings(plan);
    const hasCredits = includeCredits || creditsPerCycle > 0;
    const creditInfo = hasCredits
      ? (creditsPerCycle === 0 ? 'No credits' : `${creditsPerCycle} credits/month`)
      : 'No credits';
    
    return {
      id: plan.id,
      name: plan.name,
      monthlyPrice,
      annualPrice,
      annualMonthlyPrice,
      annualDiscount: annualDiscount > 0 ? annualDiscount : 17, // Default to 17% like in screenshot
      currency: defaultCurrency,
      features,
      creditInfo,
      // Team-specific fields
      type: (plan as SubscriptionPlan & { planType?: string; plan_type?: string }).type || 
            (plan as SubscriptionPlan & { planType?: string; plan_type?: string }).planType || 
            (plan as SubscriptionPlan & { planType?: string; plan_type?: string }).plan_type,
      includedSeats: plan.includedSeats || 
                     (plan as SubscriptionPlan & { included_seats?: number }).included_seats ||
                     1, // Default to 1 if not specified
      pricePerAdditionalSeat: monthlyPerSeatPrice, // Use the per_seat_price from the monthly pricing
      productId,
      pricings: (plan.pricesArray ?? []).map((p: Record<string, unknown>) => ({
        id: typeof p.id === 'string' ? p.id : undefined,
        billingCycleId: typeof p.billingCycleId === 'string' ? p.billingCycleId : typeof p.billing_cycle_id === 'string' ? (p.billing_cycle_id as string) : undefined,
        billingCycleName:
          typeof p.billingCycleId === 'string'
            ? billingCycleNameById.get(p.billingCycleId) ?? p.billingCycleId
            : typeof p.billing_cycle_id === 'string'
              ? billingCycleNameById.get(p.billing_cycle_id) ?? p.billing_cycle_id
              : undefined,
      })),
    };
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {t('products.subscriptionPlansList.loadError')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={visibilityFilter === 'all' ? 'secondary' : 'outline'}
          onClick={() => setVisibilityFilter('all')}
        >
          {t('products.subscriptionPlansList.visibilityAll', 'All')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={visibilityFilter === 'visible' ? 'secondary' : 'outline'}
          onClick={() => setVisibilityFilter('visible')}
        >
          {t('products.subscriptionPlansList.visibilityVisible', 'Visible')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={visibilityFilter === 'hidden' ? 'secondary' : 'outline'}
          onClick={() => setVisibilityFilter('hidden')}
        >
          {t('products.subscriptionPlansList.visibilityHidden', 'Hidden')}
        </Button>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-1/2 mb-4" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredBasePlans.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="flex flex-col items-center gap-2">
            <div className="rounded-full bg-muted p-3">
              <Package className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="font-semibold">{t('products.subscriptionPlansList.emptyTitle')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('products.subscriptionPlansList.emptyDescription')}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredBasePlans.map((basePlan) => {
            const baseVariants = variantsByParentId[basePlan.id] ?? [];
            const isDefault = basePlan.isDefault ?? basePlan.is_default ?? false;
            const isHidden = !resolveIsAvailableInCheckout(basePlan);
            const isExpanded = expandedVariants[basePlan.id] ?? false;
            return (
              <div key={basePlan.id} className="space-y-3">
                <SubscriptionPricingCard
                  plan={transformPlanData(basePlan)}
                  onEdit={() => onEditPlan(basePlan)}
                  badges={[
                    ...(isDefault
                      ? [{ label: t('products.subscriptionPlansList.defaultBadge'), variant: 'secondary' as const }]
                      : []),
                    ...(isHidden
                      ? [
                          {
                            label: t('products.subscriptionPlansList.hiddenBadge', 'Hidden'),
                            variant: 'outline' as const,
                            className: 'border-amber-500/60 text-amber-200',
                          },
                        ]
                      : []),
                  ]}
                  headerActions={
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 border-zinc-700 text-zinc-200 hover:bg-zinc-800 hover:text-white"
                        onClick={() => {
                          setVariantBasePlan(basePlan);
                          setShowVariantDialog(true);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        {t('products.subscriptionPlansList.addVariant')}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800"
                        onClick={() => setPlanToDelete(basePlan)}
                        disabled={Boolean(deleteDisabledByPlanId[basePlan.id])}
                        title={deleteDisabledByPlanId[basePlan.id] ?? t('products.subscriptionPlansList.deletePlan')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  }
                />

                <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-950/40 p-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-between text-zinc-300 hover:text-white"
                    onClick={() =>
                      setExpandedVariants((prev) => ({
                        ...prev,
                        [basePlan.id]: !isExpanded,
                      }))
                    }
                  >
                    <span>
                      {isExpanded
                        ? t('products.subscriptionPlansList.hideVariants', { count: baseVariants.length })
                        : t('products.subscriptionPlansList.showVariants', { count: baseVariants.length })}
                    </span>
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>

                  {isExpanded && (
                    <div className="mt-3 space-y-3 border-t border-zinc-800 pt-3">
                      {baseVariants.length === 0 ? (
                        <div className="text-sm text-zinc-400">
                          {t('products.subscriptionPlansList.variantsEmpty')}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {baseVariants.map((plan) => (
                            <SubscriptionPricingCard
                              key={plan.id}
                              plan={transformPlanData(plan)}
                              onEdit={() => onEditPlan(plan)}
                              badges={
                                resolveIsAvailableInCheckout(plan)
                                  ? []
                                  : [
                                      {
                                        label: t('products.subscriptionPlansList.hiddenBadge', 'Hidden'),
                                        variant: 'outline' as const,
                                        className: 'border-amber-500/60 text-amber-200',
                                      },
                                    ]
                              }
                              headerActions={
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800"
                                  onClick={() => setPlanToDelete(plan)}
                                  title={t('products.subscriptionPlansList.deletePlan')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              }
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {variantBasePlan && (
        <SubscriptionPlanVariantDialog
          productId={productId}
          basePlan={variantBasePlan}
          isOpen={showVariantDialog}
          onClose={() => {
            setShowVariantDialog(false);
            setVariantBasePlan(null);
          }}
          defaultCurrency={defaultCurrency}
          activeCurrencies={activeCurrencies}
        />
      )}
      <AlertDialog open={Boolean(planToDelete)} onOpenChange={(open) => {
        if (!open) {
          setPlanToDelete(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('products.subscriptionPlansList.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('products.subscriptionPlansList.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!planToDelete) return;
                await deletePlan.mutateAsync({ productId, planId: planToDelete.id });
                setPlanToDelete(null);
              }}
            >
              {t('products.subscriptionPlansList.deleteConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
